import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiAward, FiBookOpen, FiBarChart2, FiGift } from 'react-icons/fi';
import api from '../../services/api';
import quizApiService from '../../services/quizApi';
import { getCurrentWeekNumber } from '../../utils/datetime-comprehensive';

const PageWrapper = styled.div`
  width: 100%;
  background: #F7F5F2;
  min-height: calc(100vh - 80px);
  padding: 2rem 0;
  
  @media (max-width: 768px) {
    padding: 1.5rem 0;
  }
  
  @media (max-width: 480px) {
    padding: 1rem 0;
  }
`;

const GradesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0;
  font-weight: 400;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-top: 2rem;
  }
`;

const StatCard = styled(motion.div)`
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${props => {
    switch (props.color) {
      case 'primary': return 'rgba(216, 184, 137, 0.1)';
      case 'success': return 'rgba(56, 161, 105, 0.1)';
      case 'warning': return 'rgba(214, 158, 46, 0.1)';
      case 'accent': return 'rgba(56, 178, 172, 0.1)';
      default: return 'rgba(216, 184, 137, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.color) {
      case 'primary': return 'var(--primary-color)';
      case 'success': return 'var(--success-color)';
      case 'warning': return 'var(--warning-color)';
      case 'accent': return 'var(--accent-color)';
      default: return 'var(--primary-color)';
    }
  }};
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const StatContent = styled.div`
  flex: 1;
  text-align: right;
  
  @media (max-width: 480px) {
    text-align: left;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const Filters = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
      height: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 2px;
    }
  }
`;

const FilterButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  background-color: ${props => props.active ? 'var(--primary-color)' : '#FFFFFF'};
  color: ${props => props.active ? 'var(--text-inverse)' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '600' : '500'};
  white-space: nowrap;
  min-height: 40px;
  box-shadow: ${props => props.active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(0, 0, 0, 0.04)'};
    border-color: ${props => props.active ? 'var(--primary-color)' : 'rgba(0, 0, 0, 0.15)'};
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    min-height: 36px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Section = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 16px;
  }
`;

const SectionHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    color: var(--text-primary);
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.01em;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    
    h2 {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const CourseGradesList = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    gap: 0.75rem;
  }
`;

const CourseGradeCard = styled(motion.div)`
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.01);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border-color: rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const CourseInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CourseName = styled.h3`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.4;
  
  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
`;

const GradeDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const GradeLetter = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 32px;
  padding: 0 0.75rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 700;
  background-color: ${props => {
    const grade = props.$grade;
    if (grade === null || grade === undefined) return 'rgba(0, 0, 0, 0.08)';
    if (grade >= 90) return 'rgba(56, 161, 105, 0.15)';
    if (grade >= 80) return 'rgba(216, 184, 137, 0.15)';
    if (grade >= 70) return 'rgba(214, 158, 46, 0.15)';
    return 'rgba(239, 68, 68, 0.15)';
  }};
  color: ${props => {
    const grade = props.$grade;
    if (grade === null || grade === undefined) return 'var(--text-muted)';
    if (grade >= 90) return 'var(--success-color)';
    if (grade >= 80) return 'var(--primary-color)';
    if (grade >= 70) return 'var(--warning-color)';
    return 'var(--error-color)';
  }};
  
  @media (max-width: 480px) {
    min-width: 44px;
    height: 28px;
    padding: 0 0.625rem;
    font-size: 0.8125rem;
  }
`;

const GradePercentage = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  
  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const InstructorName = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.4;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const CourseDates = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.5;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const RecentGradesList = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.4;
    color: var(--text-muted);
  }
  
  h3 {
    color: var(--text-primary);
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    font-size: 0.875rem;
    color: var(--text-muted);
    margin: 0;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
    
    svg {
      width: 40px;
      height: 40px;
    }
    
    h3 {
      font-size: 0.9375rem;
    }
    
    p {
      font-size: 0.8125rem;
    }
  }
`;

function Grades() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState({
    overall: {
      gpa: 0,
      attendanceAverage: 0,
      totalAssignments: 0,
      completedAssignments: 0,
      change: '0'
    },
    courses: [],
    recentGrades: []
  });

  // Helper function to convert letter grade to GPA (4.0 scale)
  const letterGradeToGPA = (letter) => {
    if (!letter) return null;
    const upperLetter = letter.toUpperCase().trim();
    if (upperLetter === 'A+' || upperLetter === 'A') return 4.0;
    if (upperLetter === 'A-') return 3.7;
    if (upperLetter === 'B+') return 3.3;
    if (upperLetter === 'B') return 3.0;
    if (upperLetter === 'B-') return 2.7;
    if (upperLetter === 'C+') return 2.3;
    if (upperLetter === 'C') return 2.0;
    if (upperLetter === 'C-') return 1.7;
    if (upperLetter === 'D+') return 1.3;
    if (upperLetter === 'D') return 1.0;
    if (upperLetter === 'D-') return 0.7;
    if (upperLetter === 'F') return 0.0;
    return null;
  };

  // Helper function to convert numeric grade to GPA (4.0 scale)
  const numericGradeToGPA = (numericGrade) => {
    if (numericGrade === null || numericGrade === undefined || isNaN(Number(numericGrade))) {
      return null;
    }
    const grade = Number(numericGrade);
    if (grade >= 97) return 4.0; // A+
    if (grade >= 93) return 4.0; // A
    if (grade >= 90) return 3.7; // A-
    if (grade >= 87) return 3.3; // B+
    if (grade >= 83) return 3.0; // B
    if (grade >= 80) return 2.7; // B-
    if (grade >= 77) return 2.3; // C+
    if (grade >= 73) return 2.0; // C
    if (grade >= 70) return 1.7; // C-
    if (grade >= 67) return 1.3; // D+
    if (grade >= 63) return 1.0; // D
    if (grade >= 60) return 0.7; // D-
    return 0.0; // F
  };

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        setLoading(true);
        
        // Fetch all enrolled courses, course final grades, assignments, and quizzes in parallel
        const [allCoursesResponse, courseGradesResponse, assignmentsResponse, quizzesResponse] = await Promise.all([
          api.getCourses(), // Get all enrolled courses (past and present)
          api.getCourseFinalGrades(), // Get courses with final grades assigned
          api.getAssignments(),
          quizApiService.getStudentAssignments({ limit: 1000 }) // Fetch all quizzes
        ]);
        
        // Get enrolled course IDs from all courses (these are courses the student is enrolled in)
        const enrolledCourseIds = new Set();
        if (allCoursesResponse.success && allCoursesResponse.courses) {
          allCoursesResponse.courses.forEach(course => {
            if (course.id) {
              enrolledCourseIds.add(course.id);
            }
          });
        }
        
        // Create a map of course grades for quick lookup
        const courseGradesMap = new Map();
        if (courseGradesResponse.success && courseGradesResponse.courseGrades) {
          courseGradesResponse.courseGrades.forEach(cg => {
            if (cg.courseId) {
              courseGradesMap.set(cg.courseId, cg);
            }
          });
        }
        
        // Process assignments - only include assignments from active courses that student is enrolled in
        let totalAssignments = 0;
        let completedAssignments = 0;
        let assignmentScores = [];

        if (assignmentsResponse.success && assignmentsResponse.assignments) {
          // Filter to only include assignments from active courses that student is enrolled in
          const assignments = assignmentsResponse.assignments.filter(assignment => {
            const isActiveCourse = assignment.course_is_active === true || assignment.course_is_active === 1;
            const isEnrolled = !assignment.course_id || enrolledCourseIds.has(assignment.course_id);
            return isActiveCourse && isEnrolled;
          });

          assignments.forEach(assignment => {
            // Handle weekly assignments - count each week as a separate task
            if (assignment.allow_weekly_submissions) {
              // Calculate total weeks based on start_date
              let totalWeeks = 0;
              if (assignment.start_date) {
                const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
                const currentWeek = getCurrentWeekNumber(assignment.start_date, null, breakPeriods);
                const parsedMaxWeeks = Number(assignment.max_weekly_submissions);
                const maxWeeks = Number.isFinite(parsedMaxWeeks) && parsedMaxWeeks > 0
                  ? Math.floor(parsedMaxWeeks)
                  : 1;
                totalWeeks = Math.min(currentWeek > 0 ? currentWeek : 0, maxWeeks);
              }

              const submittedWeeks = assignment.submitted_week_numbers || [];
              const weeklyOverridesByWeek = assignment?.weekly_overrides_by_week && typeof assignment.weekly_overrides_by_week === 'object'
                ? assignment.weekly_overrides_by_week
                : {};
              const requiredWeeks = Array.from({ length: totalWeeks }, (_, index) => index + 1)
                .filter(weekNumber => {
                  const weekOverride = weeklyOverridesByWeek[weekNumber] ?? weeklyOverridesByWeek[String(weekNumber)];
                  return !(weekOverride?.is_extra_credit === true || weekOverride?.is_extra_credit === 1);
                });

              // Add total weeks to task count
              totalAssignments += requiredWeeks.length;

              // Add completed weeks to completed count
              completedAssignments += submittedWeeks.filter(weekNumber => requiredWeeks.includes(Number(weekNumber))).length;
            } else {
              // Non-weekly assignment - count as 1 task
              totalAssignments += 1;

              // Count as completed if submitted
              if (assignment.submitted_at) {
                completedAssignments++;
              }
            }

            // Collect graded assignment scores for average calculation
            if (assignment.grade !== null && assignment.grade !== undefined && assignment.points) {
              if (assignment.is_extra_credit === true || assignment.is_extra_credit === 1) {
                return;
              }

              const score = parseFloat(assignment.grade);
              const maxPoints = parseFloat(assignment.points);
              if (!isNaN(score) && !isNaN(maxPoints) && maxPoints > 0) {
                assignmentScores.push((score / maxPoints) * 100);
              }
            }
          });
        }
        
        // Process quizzes - only include quizzes from active courses that student is enrolled in (or standalone quizzes with no course)
        let totalQuizzes = 0;
        let completedQuizzes = 0;
        let quizScores = [];
        
        if (quizzesResponse.success && quizzesResponse.data) {
          // Filter to only include quizzes from active courses that student is enrolled in, or standalone quizzes (no course_id)
          const quizzes = quizzesResponse.data.filter(quiz => {
            // Standalone quizzes (no course) are included
            if (!quiz.course_id) {
              return true;
            }
            // For course-based quizzes, must be from active course and student must be enrolled
            const isActiveCourse = quiz.course_is_active === true || quiz.course_is_active === 1;
            const isEnrolled = enrolledCourseIds.has(quiz.course_id);
            return isActiveCourse && isEnrolled;
          });
          totalQuizzes = quizzes.length;
          
          quizzes.forEach(quiz => {
            // Count as completed if status is 'completed'
            if (quiz.status === 'completed') {
              completedQuizzes++;
            }

            // Collect quiz scores for average calculation
            if (quiz.score !== null && quiz.score !== undefined && quiz.max_score) {
              const score = parseFloat(quiz.score);
              const maxScore = parseFloat(quiz.max_score);
              if (!isNaN(score) && !isNaN(maxScore) && maxScore > 0) {
                quizScores.push((score / maxScore) * 100);
              }
            }
          });
        }
        
        // Calculate averages
        const quizAverage = quizScores.length > 0
          ? Math.round(quizScores.reduce((sum, score) => sum + score, 0) / quizScores.length)
          : 0;
        
        const assignmentAverage = assignmentScores.length > 0
          ? Math.round(assignmentScores.reduce((sum, score) => sum + score, 0) / assignmentScores.length)
          : 0;
        
        // Calculate tasks completed
        const totalTasks = totalAssignments + totalQuizzes;
        const completedTasks = completedAssignments + completedQuizzes;
        
        // Process all enrolled courses for GPA calculation (past and present)
        let coursesWithStats = [];
        let gpa = 0;
        
        if (allCoursesResponse.success && allCoursesResponse.courses) {
          // Process all enrolled courses
          coursesWithStats = allCoursesResponse.courses.map((course) => {
            // Get grade information from courseGradesMap if available
            const cg = courseGradesMap.get(course.id);
            const gradeValue = cg ? cg.grade : (course.enrollment_grade || null);
            const numericGradeValue = cg ? cg.numericGrade : null;
            
            const isNumeric = !isNaN(Number(gradeValue)) && gradeValue !== null && gradeValue !== '';
            const numericGrade = numericGradeValue !== null && numericGradeValue !== undefined 
              ? Number(numericGradeValue) 
              : (isNumeric ? Number(gradeValue) : null);
            
            // Convert numeric grade to letter grade if needed
            let letter = '';
            if (isNumeric) {
              if (numericGrade >= 97) letter = 'A+';
              else if (numericGrade >= 93) letter = 'A';
              else if (numericGrade >= 90) letter = 'A-';
              else if (numericGrade >= 87) letter = 'B+';
              else if (numericGrade >= 83) letter = 'B';
              else if (numericGrade >= 80) letter = 'B-';
              else if (numericGrade >= 77) letter = 'C+';
              else if (numericGrade >= 73) letter = 'C';
              else if (numericGrade >= 70) letter = 'C-';
              else if (numericGrade >= 67) letter = 'D+';
              else if (numericGrade >= 63) letter = 'D';
              else if (numericGrade >= 60) letter = 'D-';
              else letter = 'F';
            } else if (gradeValue) {
              // It's already a letter grade
              letter = gradeValue.toUpperCase();
            }
            
            return {
              id: course.id,
              name: course.name,
              code: course.code,
              grade: numericGrade || (gradeValue && !isNaN(parseFloat(gradeValue)) ? parseFloat(gradeValue) : null),
              letter: letter || (gradeValue ? gradeValue.toUpperCase() : 'N/A'),
              gradeValue: gradeValue, // Keep original grade value for filtering
              numericGrade: numericGrade,
              startDate: course.start_date,
              endDate: course.end_date,
              instructorName: course.instructor_first_name && course.instructor_last_name
                ? `${course.instructor_first_name} ${course.instructor_last_name}`
                : null
            };
          });
          
          // Calculate GPA from courses with final grades assigned only
          // Filter to only include courses with final grades
          const coursesWithGrades = coursesWithStats.filter(c => 
            c.gradeValue !== null && c.gradeValue !== undefined && c.gradeValue !== ''
          );
          
          const gpaValues = coursesWithGrades.map(c => {
            // Try to get GPA from letter grade first
            if (c.letter && c.letter !== 'N/A') {
              const gpaFromLetter = letterGradeToGPA(c.letter);
              if (gpaFromLetter !== null) return gpaFromLetter;
            }
            // Fall back to numeric grade conversion
            if (c.numericGrade !== null && c.numericGrade !== undefined) {
              return numericGradeToGPA(c.numericGrade);
            }
            return null;
          }).filter(gpa => gpa !== null);
          
          // Calculate average GPA from courses with final grades only
          gpa = gpaValues.length > 0
            ? gpaValues.reduce((sum, g) => sum + g, 0) / gpaValues.length
            : 0;
        } else if (courseGradesResponse.success) {
          // Fallback: if allCoursesResponse fails, use courseGradesResponse
          const courseGrades = courseGradesResponse.courseGrades || [];
          
          coursesWithStats = courseGrades.map((cg) => {
            const gradeValue = cg.grade;
            const isNumeric = !isNaN(Number(gradeValue)) && gradeValue !== null && gradeValue !== '';
            const numericGrade = cg.numericGrade !== null && cg.numericGrade !== undefined 
              ? Number(cg.numericGrade) 
              : (isNumeric ? Number(gradeValue) : null);
            
            let letter = '';
            if (isNumeric) {
              if (numericGrade >= 97) letter = 'A+';
              else if (numericGrade >= 93) letter = 'A';
              else if (numericGrade >= 90) letter = 'A-';
              else if (numericGrade >= 87) letter = 'B+';
              else if (numericGrade >= 83) letter = 'B';
              else if (numericGrade >= 80) letter = 'B-';
              else if (numericGrade >= 77) letter = 'C+';
              else if (numericGrade >= 73) letter = 'C';
              else if (numericGrade >= 70) letter = 'C-';
              else if (numericGrade >= 67) letter = 'D+';
              else if (numericGrade >= 63) letter = 'D';
              else if (numericGrade >= 60) letter = 'D-';
              else letter = 'F';
            } else if (gradeValue) {
              letter = gradeValue.toUpperCase();
            }
            
            return {
              id: cg.courseId,
              name: cg.courseName,
              code: cg.courseCode,
              grade: numericGrade || (gradeValue && !isNaN(parseFloat(gradeValue)) ? parseFloat(gradeValue) : null),
              letter: letter || (gradeValue ? gradeValue.toUpperCase() : 'N/A'),
              gradeValue: gradeValue,
              numericGrade: numericGrade,
              startDate: cg.startDate,
              endDate: cg.endDate,
              instructorName: cg.instructorName
            };
          });
          
          // Calculate GPA from courses with final grades assigned only
          // Filter to only include courses with final grades
          const coursesWithGrades = coursesWithStats.filter(c => 
            c.gradeValue !== null && c.gradeValue !== undefined && c.gradeValue !== ''
          );
          
          const gpaValues = coursesWithGrades.map(c => {
            if (c.letter && c.letter !== 'N/A') {
              const gpaFromLetter = letterGradeToGPA(c.letter);
              if (gpaFromLetter !== null) return gpaFromLetter;
            }
            if (c.numericGrade !== null && c.numericGrade !== undefined) {
              return numericGradeToGPA(c.numericGrade);
            }
            return null;
          }).filter(gpa => gpa !== null);
          
          // Calculate average GPA from courses with final grades only
          gpa = gpaValues.length > 0
            ? gpaValues.reduce((sum, g) => sum + g, 0) / gpaValues.length
            : 0;
        }
        
        // Filter to only show courses with final grades assigned for display
        const courses = coursesWithStats.filter(c =>
          c.gradeValue !== null && c.gradeValue !== undefined && c.gradeValue !== ''
        );

        // Calculate attendance average for all active courses
        let attendanceAverage = 0;
        if (allCoursesResponse.success && allCoursesResponse.courses) {
          try {
            const activeCourses = allCoursesResponse.courses.filter(c => c.is_active === 1 || c.is_active === true);
            const attendanceResponses = await Promise.allSettled(
              activeCourses.map(course => api.getStudentAttendance(course.id))
            );

            const attendancePercentages = [];
            attendanceResponses.forEach((response) => {
              if (response.status === 'fulfilled' && response.value?.success && response.value?.attendance) {
                const att = response.value.attendance;
                const stats = att.statistics || {};
                const attendanceRate = stats.attendanceRate;
                if (attendanceRate !== undefined && attendanceRate !== null) {
                  attendancePercentages.push(attendanceRate);
                }
              }
            });

            if (attendancePercentages.length > 0) {
              attendanceAverage = Math.round(attendancePercentages.reduce((sum, pct) => sum + pct, 0) / attendancePercentages.length);
            }
          } catch (error) {
            console.error('Error calculating attendance average:', error);
            attendanceAverage = 0;
          }
        }

        setGrades({
          overall: {
            gpa: Math.round(gpa * 100) / 100, // Round to 2 decimal places
            attendanceAverage: attendanceAverage,
            quizAverage: quizAverage,
            assignmentAverage: assignmentAverage,
            totalTasks: totalTasks,
            completedTasks: completedTasks,
            change: '0' // TODO: Calculate change from previous term if needed
          },
          courses: courses,
          recentGrades: []
        });
      } catch (error) {
        console.error('Error fetching grades:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGrades();
  }, []);

  const filteredCourses = grades.courses.filter(course => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'a') return course.letter && course.letter.toUpperCase().startsWith('A');
    if (activeFilter === 'b') return course.letter && course.letter.toUpperCase().startsWith('B');
    if (activeFilter === 'c') return course.letter && course.letter.toUpperCase().startsWith('C');
    return true;
  });

  return (
    <PageWrapper>
      <GradesContainer>
        <Header>
          <Title>My Grades</Title>
          <Subtitle>Track your academic performance and progress</Subtitle>
        </Header>

      <Filters>
        <FilterButton
          active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
        >
          All Grades
        </FilterButton>
        <FilterButton
          active={activeFilter === 'a'}
          onClick={() => setActiveFilter('a')}
        >
          A Grades
        </FilterButton>
        <FilterButton
          active={activeFilter === 'b'}
          onClick={() => setActiveFilter('b')}
        >
          B Grades
        </FilterButton>
        <FilterButton
          active={activeFilter === 'c'}
          onClick={() => setActiveFilter('c')}
        >
          C Grades
        </FilterButton>
      </Filters>

      <ContentGrid>
        <Section>
          <SectionHeader>
            <h2>Course Grades</h2>
          </SectionHeader>
          <CourseGradesList>
            {loading ? (
              <EmptyState>
                <FiAward />
                <h3>Loading grades...</h3>
              </EmptyState>
            ) : filteredCourses.length > 0 ? (
              filteredCourses.map((course, index) => (
                <CourseGradeCard
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <CourseInfo>
                    <CourseName>
                      {course.name} ({course.code})
                    </CourseName>
                    {course.gradeValue && course.gradeValue !== null && course.gradeValue !== undefined && course.gradeValue !== '' && (
                      <GradeDisplay>
                        <GradeLetter $grade={course.numericGrade}>
                          {course.letter || course.gradeValue}
                        </GradeLetter>
                        {course.numericGrade !== null && course.numericGrade !== undefined && (
                          <GradePercentage>
                            {Math.round(course.numericGrade)}%
                          </GradePercentage>
                        )}
                      </GradeDisplay>
                    )}
                    {course.instructorName && (
                      <InstructorName>
                        Instructor: {course.instructorName}
                      </InstructorName>
                    )}
                    {(course.startDate || course.endDate) && (
                      <CourseDates>
                        {course.startDate && (
                          <span>
                            Start: {new Date(course.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                        {course.startDate && course.endDate && ' • '}
                        {course.endDate && (
                          <span>
                            End: {new Date(course.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </CourseDates>
                    )}
                  </CourseInfo>
                </CourseGradeCard>
              ))
            ) : (
              <EmptyState>
                <FiAward />
                <h3>No grades found</h3>
                <p>Try adjusting your filters to see your grades.</p>
              </EmptyState>
            )}
          </CourseGradesList>
        </Section>

        <Section>
          <SectionHeader>
            <h2>Cumulative GPA</h2>
          </SectionHeader>
          <RecentGradesList>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem 2rem',
                textAlign: 'center',
                gap: '0.5rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'center' }}>
                <div style={{ color: 'var(--primary-color)' }}>
                  <FiGift size={32} />
                </div>
                <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                  {grades.overall.gpa.toFixed(1)}
                </div>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#d32f2f' }}>
                excludes current term
              </div>
            </motion.div>
          </RecentGradesList>
        </Section>
      </ContentGrid>

      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <StatHeader>
            <StatIcon color="primary">
              <FiAward size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{grades.overall.attendanceAverage}%</StatValue>
              <StatLabel>Attendance Average</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <StatHeader>
            <StatIcon color="success">
              <FiAward size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{grades.overall.quizAverage}%</StatValue>
              <StatLabel>Quiz Average Scores</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <StatHeader>
            <StatIcon color="accent">
              <FiBookOpen size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{grades.overall.assignmentAverage}%</StatValue>
              <StatLabel>Assignment Average Scores</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <StatHeader>
            <StatIcon color="warning">
              <FiBarChart2 size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>
                {grades.overall.totalTasks > 0 
                  ? `${grades.overall.completedTasks}/${grades.overall.totalTasks}`
                  : '0/0'
                }
              </StatValue>
              <StatLabel>Tasks Completed</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>
      </StatsGrid>
      </GradesContainer>
    </PageWrapper>
  );
}

export default Grades;
